'use client'

import React, { useState } from 'react'
import Image from 'next/image'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { CTAButton } from '@/components/ui/cta-button'
import { mapLegacyAppearance, type LinkFieldType } from '@/fields/link'
import { resolveHref } from '@/utilities/resolveHref'

type CardStyleLargeProps = {
  title: string
  description?: string
  icon?: { url?: string; filename?: string; updatedAt?: string } | null // Media relationship from Payload
  cta?: LinkFieldType
  cardIndex: number
  isHovered?: boolean
  onHover?: (isHovered: boolean) => void
  // Height synchronization props for desktop alignment
  titleRef?: (el: HTMLDivElement | null) => void
  descriptionRef?: (el: HTMLDivElement | null) => void
  titleHeight?: number
  descriptionHeight?: number
}

export const CardStyleLarge: React.FC<CardStyleLargeProps> = ({
  title,
  description,
  icon,
  cta,
  cardIndex: _cardIndex,
  isHovered = false,
  onHover,
  titleRef,
  descriptionRef,
  titleHeight,
  descriptionHeight,
}) => {
  const [localHovered, setLocalHovered] = useState(false)
  const hovered = isHovered || localHovered

  const handleMouseEnter = () => {
    setLocalHovered(true)
    onHover?.(true)
  }

  const handleMouseLeave = () => {
    setLocalHovered(false)
    onHover?.(false)
  }

  // Get icon URL with cache tag
  const iconUrl = icon?.url || icon?.filename
    ? getMediaUrl(icon.url || `/media/${icon.filename}`, icon.updatedAt)
    : null

  return (
    <div
      className={`box-border content-stretch flex flex-col p-6 relative rounded-[16px] w-full h-[418px] border transition-colors duration-300 ease-in-out ${
        hovered
          ? 'bg-civo-cloud border-civo-cloud'
          : 'bg-civo-white border-civo-cloud'
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="flex flex-col h-[370px] justify-between w-full">
        <div className="flex items-start">
          {iconUrl && (
            <div
              className="relative size-[120px] transition-all duration-300 ease-in-out origin-top-left"
            >
              <Image
                src={iconUrl}
                alt={title}
                fill
                className="object-contain"
                sizes="120px"
              />
            </div>
          )}
        </div>

        <div className="flex flex-col">
          <div
            ref={titleRef}
            className="subheading-large text-black"
            style={titleHeight ? { height: `${titleHeight}px` } : undefined}
          >
            <p className="leading-7 mb-0">{title}</p>
          </div>
          {description && (
            <div
              ref={descriptionRef}
              className="body-medium text-black/70 mt-4"
              style={descriptionHeight ? { height: `${descriptionHeight}px` } : undefined}
            >
              <p className="leading-[22px] mb-0">{description}</p>
            </div>
          )}
          {cta?.label && (cta?.url || cta?.reference) ? (
            <div className="mt-6">
              <CTAButton
                variant={mapLegacyAppearance(cta.appearance)}
                href={resolveHref(cta)}
                newTab={cta.newTab || false}
              >
                {cta.label}
              </CTAButton>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}